import { ECamera } from '../enums/camera.enum';
import { Injectable } from '@angular/core';
import { Replay } from '../models/winco/replay.model';
import { environment } from 'src/environments/environment';
import { Camera } from '../models/winco/camera.model';
import { ECameraFunction } from '../enums/camera-function.enum';
import { HttpClientService } from './http-client.service';
import { EventManagerService } from 'event-manager';
import { WincoEvents } from '../enums/winco-events..enum';
import { Router } from '@angular/router';
import { AuthWincoService } from './auth.service';
import { ETypeUser } from '../enums/type-user.enum';
import { EError } from '../enums/error.enum';

/**
 * Servicio que da soporte al componente Camera
 * Se encarga de gestionar los procesos de las cámaras de visualización (principal y auxiliar)
 */
@Injectable({
  providedIn: 'root'
})
export class CameraService
{
  private stream:MediaStream; //stream de video que se está reproduciendo actualmente

  public cameras: Camera[];

  //identificador de la cámara auxiliar
  //public auxiliarCamera: string;

  public systemCamerasConnected = false;
  public streamStarted = false;

  //variables para el control de reproducción
  public hasMultiCamera = false;

  //camara activa para la visualización en tiempo real
  public camera:ECamera;

  showingInfo: boolean;//indica que el viewer está mostrando la pantalla de información previa al ejercicio

  constructor(private clientService: HttpClientService,
              private eventManagerService: EventManagerService,
              private router:Router,
              private authService:AuthWincoService)
  {}

  /**
   * Obtiene la lista de cámaras conectadas al dispositivo
   * @returns Lista de cámaras conectadas
   */
  public initialize(): Promise<void>
  {
    return new Promise<void>((resolve, reject) =>
    {
      this.clientService.get<Camera[]>("Camera/GetCameras").then(cameras =>
      {
        this.getMediaDevices().then((devices) =>
        {
          cameras.map(camera =>
          {
            if(camera.enabled)
            {
              const device = devices.find(m => m.label.toUpperCase().indexOf(camera.name.toUpperCase()) >= 0);

              if(device)
              {
                camera.idDevice = device.deviceId;
                camera.type = (camera.function === ECameraFunction.AUXILIAR) ? ECamera.AUXILIAR  : ((camera.function === ECameraFunction.VIEWER) ? ECamera.SYSTEM : undefined);
                camera.constraint = {
                  audio: false,
                  video: {
                    width: { ideal: camera.width },
                    height: { ideal: camera.height },
                    deviceId: { exact: camera.idDevice }
                  }
                };

                if(camera.function === ECameraFunction.AUXILIAR)
                  this.hasMultiCamera = true;
              }
              else
              {
                camera.enabled = false;
              }
            }
          });

          this.cameras = cameras;
          this.camera = ECamera.SYSTEM;

          this.addListenerCameras();

          resolve();
        })
        .catch(err => reject(err));
      })
      .catch(err => reject(err));
    });
  }

  getMediaDevices(): Promise<MediaDeviceInfo[]>
  {
    return new Promise<MediaDeviceInfo[]>((resolve, reject) =>
    {
      try
      {
        if ('mediaDevices' in navigator && navigator.mediaDevices)
        {
          //electron falla al obtener los dispositivos si hacemos getUserMedia antes de enumerar los dispositivos
          if(environment.camerasEnabled)
          {
            navigator.mediaDevices.enumerateDevices().then((devices) =>
              resolve(devices.filter(m => m.kind == "videoinput")))
            .catch(err =>
            {
              console.error('[CameraService] Error enumerating media devices.', err);
              reject(err);
            });
          }
          else
          {
            navigator.mediaDevices.getUserMedia({video: true}).then(stream =>
            {
              navigator.mediaDevices.enumerateDevices().then((devices) =>
              {
                stream.getTracks().forEach(track => track.stop());
                resolve(devices.filter(m => m.kind == "videoinput"));
              })
              .catch(err =>
              {
                stream.getTracks().forEach(track => track.stop());
                console.error('[CameraService] Error enumerating media devices after requesting webcam access.', err);
                reject(err);
              });
            })
            .catch(err =>
            {
              console.error('[CameraService] Error requesting temporary webcam access to enumerate devices.', err);
              reject(err);
            });
          }
        }
        else
        {
          reject();
        }
      }
      catch(err)
      {
        reject(err);
      }
    });
  }

  checkCameras(): Promise<void>
  {
    return new Promise<void>((resolve, reject) =>
    {
      this.systemCamerasConnected = false;

      if(environment.camerasEnabled)
      {
        const cameraViewer = this.cameras.find(m => m.function === ECameraFunction.VIEWER);
        const cameraTracking = this.cameras.find(m => m.function === ECameraFunction.TRACKING);

        if(cameraViewer.enabled && cameraTracking.enabled)
        {
          this.systemCamerasConnected = true;
          resolve();
        }
        else if(cameraViewer.enabled)
        {
          reject(ECameraFunction.TRACKING);
        }
        else if(cameraTracking.enabled)
        {
          reject(ECameraFunction.VIEWER);
        }
        else
        {
          reject();
        }
      }
      else
      {
        const cameraWeb = this.cameras.find(m => m.function === ECameraFunction.WEBCAM && m.enabled);

        if(cameraWeb.enabled)
        {
          this.systemCamerasConnected = true;
          resolve();
        }
        else
        {
          reject();
        }
      }
    });
  }

  setStream(constraint:any): Promise<void>
  {
    return new Promise<void>((resolve, reject) =>
    {
      navigator.mediaDevices.getUserMedia(constraint).then(stream =>
      {
        this.stream = stream;
        resolve();
      })
      .catch(err =>
      {
        console.error('[CameraService] Error opening camera stream.', { constraint, err });
        reject(err);
      });
    });
  }

  /**
   * Devuelve el stream de la cámara principal de visualización actual
   * @returns MediaStream
   */
  getStream()
  {
    return this.stream;
  }

  setCameraViewer() : Promise<void>
  {
    const camera = (environment.camerasEnabled) ? this.cameras.find(m => m.function === ECameraFunction.VIEWER)
                                            : this.cameras.find(m => m.function === ECameraFunction.WEBCAM && m.enabled);

    return this.setStream(camera.constraint);
  }

  setCameraTracking()
  {
    return new Promise<void>((resolve, reject) =>
    {
      const camera = (environment.camerasEnabled) ? this.cameras.find(m => m.function === ECameraFunction.TRACKING) : undefined;

      if(camera)
      {
        this.initializeAnalysis(camera).then(() =>
        {
          resolve();
        })
        .catch(err => reject(err));
      }
      else
      {
        resolve();
      }
    });
  }

  /**
   * Activa el manejador de eventos de dispositivos
   */
  public addListenerCameras()
  {
    //añadimos un manejador de eventos al evento 'devicechange'
    //primero limpiamos el manejador para evitar duplicados
    // this.removeListenerCameras();

    navigator.mediaDevices.ondevicechange = () => this.listenCameras();
  }

  /**
   * Desactiva el manejador de eventos de dispositivos
   */
  public removeListenerCameras()
  {
    navigator.mediaDevices.ondevicechange = () => null;
  }

  private listenCameras()
  {
    //se ha conectado o desconectado una cámara

    const camViewer = this.cameras.find(m => m.function === ECameraFunction.VIEWER),
          camTracking = this.cameras.find(m => m.function === ECameraFunction.TRACKING);

    let added:Camera,
        removed:Camera,
        navigate:string[];

    setTimeout(() =>
    {
      this.clientService.get<Camera[]>("Camera/GetCameras").then(cameras =>
      {
        //actualizamos los ids de todas las camaras
        cameras.map(camera => this.cameras.find(m => m.function === camera.function).idCamera = camera.idCamera);

        this.getMediaDevices().then(async devices =>
        {
          //comprobamos si se han conectado nuevas camaras
          devices.map(async device =>
          {
            let camera = this.cameras.find(m => device.label.toUpperCase() === (m.name + ' (' + m.vid + ':' + m.pid + ')').toUpperCase());

            if(!camera)
              camera = this.cameras.find(m => device.label.toUpperCase() === m.name.toUpperCase());

            if(camera && !camera.enabled)
            {
              camera.enabled = true;
              camera.idDevice = device.deviceId;
              camera.constraint = {
                audio: false,
                video: {
                  width: { ideal: camera.width },
                  height: { ideal: camera.height },
                  deviceId: { exact: camera.idDevice }
                }
              };

              if(camera.function === ECameraFunction.AUXILIAR)
              {
                this.camera = ECamera.SYSTEM;
                this.hasMultiCamera = true;
                this.eventManagerService.emitEvent(WincoEvents.CONNECT_AUXILIAR_CAMERA);
              }
              else if(environment.camerasEnabled)
              {
                if(camera.function === ECameraFunction.VIEWER)
                {
                  added = camera;
                  this.camera = ECamera.SYSTEM;
                  this.systemCamerasConnected = true;
                  await this.setStream(camera.constraint);
                }
                else if(camera.function === ECameraFunction.TRACKING)
                {
                  added = camera;
                }
              }
            }
          });

          //comprobamos si se ha desconectado una camara
          this.cameras.map(async camera =>
          {
            let device = devices.find(m => m.label.toUpperCase() === (camera.name + ' (' + camera.vid + ':' + camera.pid + ')').toUpperCase());

            if(!device)
              device = devices.find(m => m.label.toUpperCase() === camera.name.toUpperCase());

            if(!device && camera.enabled)
            {
              camera.enabled = false;
              camera.idDevice = undefined;
              camera.constraint = undefined

              if(camera.function === ECameraFunction.AUXILIAR)
              {
                this.camera = ECamera.SYSTEM;
                this.hasMultiCamera = false;
                this.eventManagerService.emitEvent(WincoEvents.DISCONNECT_AUXILIAR_CAMERA);
              }
              else if(environment.camerasEnabled)
              {
                if(camera.function === ECameraFunction.VIEWER)
                {
                  removed = camera;
                  this.camera = undefined;
                  this.systemCamerasConnected = false;
                  this.stream = null;
                }
                else if(camera.function === ECameraFunction.TRACKING)
                {
                  removed = camera;
                }
              }
            }
          });


          if(added)
          {
            if((added.function === ECameraFunction.TRACKING || added.function === ECameraFunction.VIEWER))
            {
              await this.finalizeAnalysis();

              if(camTracking.enabled)
                await this.initializeAnalysis(this.cameras.find(m => m.function === ECameraFunction.TRACKING));
            }

            if(camViewer.enabled && camTracking.enabled)
            {
              const user = this.authService.getUser();
              navigate = (user) ? ((user.idType == ETypeUser.STUDENT) ? ['courses', 'list'] : ['administration', 'groups', 'list']) : ['account', 'login'];
            }
          }
          else if(removed)
          {
            if((removed.function === ECameraFunction.TRACKING || removed.function === ECameraFunction.VIEWER))
            {
              await this.finalizeAnalysis();

              if(camTracking.enabled)
                await this.initializeAnalysis(this.cameras.find(m => m.function === ECameraFunction.TRACKING));

              navigate = ['errors', 'error', (removed.function === ECameraFunction.TRACKING) ? EError.TRACKING_FAILED : EError.VIEWER_FAILED];
            }
          }

          if(navigate)
            this.router.navigate(['/', ...navigate]);
        });
      });
    }, 250);
  }

  /**
   * Redimensiona el visor de la cámara
   */
  public resizeCamera()
  {
    this.eventManagerService.emitEvent(WincoEvents.RESIZE_CAMERA);
  }

  /**
   * Cambia la vista de la cámara principal a la cámara auxiliar
   * @param camera ECamera - Cámara que debe ser mostrada
   */
  public changeCamera(camera:ECamera)
  {
    this.eventManagerService.emitEvent(WincoEvents.CHANGE_CAMERA, camera);
  }

  /**
   * Inicia el tracking de un ejercicio
   * @returns Promise<any>
   */
  public startAnalysis(): Promise<any>
  {
    return  (environment.camerasEnabled) ?
              this.clientService.get("Camera/StartAnalysis")  :
              new Promise<any>((resolve) => resolve(null));
  }

  /**
   * Finaliza el tracking de un ejercicio
   * @returns Promise<Replay> - Devuelve los datos obtenidos de trackeo
   */
  public stopAnalysis(save:boolean): Promise<Replay>
  {
    return this.clientService.get<Replay>("Camera/StopAnalysis/" + ((save) ? 'true' : 'false') );
  }

  /**
   * Pausa el tracking de un ejercicio
   * @returns Promise<any>
   */
  public pauseAnalysis(): Promise<any>
  {
    return this.clientService.get("Camera/PauseAnalysis");
  }

  /**
   * Reanuda el tracking de un ejercicio
   * @returns Promise<any>
   */
  public resumeAnalysis(): Promise<any>
  {
    return this.clientService.get("Camera/ResumeAnalysis");
  }

  /**
   * Inicializa la librería de Nuavis
   * @returns Promise<any>
   */
  public initializeAnalysis(camera:Camera): Promise<any>
  {
    return this.clientService.post("Camera/InitializeAnalysis", camera);
  }

  /**
   * Detiene todos los procesos de la librería de Nuavis
   * @returns Promise<any>
   */
  public finalizeAnalysis(): Promise<any>
  {
    return this.clientService.get("Camera/FinalizeAnalysis");
  }

  /**
   * Bloquea la cámara de tracking
   * @returns Promise<any>
   */
  public lockCamera(): Promise<any>
  {
    return this.clientService.get("Camera/LockCamera");
  }

  /**
   * Desbloquea la cámara de tracking
   * @returns Promise<any>
   */
  public unlockCamera(): Promise<any>
  {
    return this.clientService.get("Camera/UnlockCamera");
  }

  /**
   * Inicia la grabación de un vídeo
   * @returns Promise<any>
   */
  public startRecord(): Promise<any>
  {
    return  (environment.camerasEnabled) ?
    this.clientService.get("Camera/StartRecord")  :
    new Promise<any>((resolve) => resolve(null));
  }

  /**
   * Finaliza la grabación de un vídeo
   * @returns Promise<any>
   */
  public stopRecord(): Promise<any>
  {
    return this.clientService.get("Camera/StopRecord");
  }

  /**
   * Pausa la grabación de un vídeo
   * @returns Promise<any>
   */
  public pauseRecord(): Promise<any>
  {
    return this.clientService.get("Camera/PauseRecord");
  }

  /**
   * Reanuda la grabación de un vídeo
   * @returns Promise<any>
   */
  public resumeRecord(): Promise<any>
  {
    return this.clientService.get("Camera/ResumeRecord");
  }

  // newChangeCamera()
  // {
  //   this.clientService.get("Camera/ChangeCamera").then();
  // }
}
